import { TextSelection, Transaction } from 'prosemirror-state';
import { Node, DOMParser, Schema } from 'prosemirror-model';
import { EditorContentConfiguration } from '../configurations/editor-content';
import { EditorView } from 'prosemirror-view';
import { ContentBuilder } from './content-builder';
import { wrapInList, liftListItem, splitListItem } from "prosemirror-schema-list";
// import { splitBlockKeepMarks } from "prosemirror-commands";


export class ContentTransformer {
    private contentBuilder: ContentBuilder;

    constructor(schema: Schema) {
        this.contentBuilder = new ContentBuilder(schema);
    }

    updateDocumentAttribute(editorView: EditorView, attribute: any) {
        const transaction: Transaction = editorView.state.tr;
        transaction.doc.descendants((node: Node, pos: number, parent: Node) => {
            this.updateNodeAttribute(attribute.name, attribute.value, node, pos, transaction);
            /* Doesn't descend into a node when the callback returns false. */
            return true;
        });
        editorView.dispatch(transaction);
    }


    updateCurrentParagraph(newContentConfiguration: EditorContentConfiguration, editorView: EditorView) {
        const transaction = editorView.state.tr;
        const selection = editorView.state.selection;
        editorView.state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            let attrs = {
                textAlign: newContentConfiguration.paragraphAlign,
                fontWeight: newContentConfiguration.paragraphFontWeight[0],
                fontStyle: newContentConfiguration.paragraphFontStyle[0],
                textDecoration: newContentConfiguration.paragraphTextDecoration[0],
            };
            attrs = { ...node.attrs, ...attrs }; // left, right => right
            if (node.type.name !== 'text') {
                transaction.setNodeMarkup(pos, node.type, attrs, node.marks);
            }
        });
        transaction.scrollIntoView();
        editorView.dispatch(transaction);
    }

    private updateNodeAttribute(attributeName: string, attributeValue: string, node: Node, pos: number, transaction: Transaction) {
        if (node.attrs && node.attrs[attributeName]) {
            const currentValue = node.attrs[attributeName];
            if (currentValue !== attributeValue) {
                const newAttrs = { ...node.attrs };
                newAttrs[attributeName] = attributeValue;
                transaction.setNodeMarkup(pos, node.type, newAttrs);
            }
        }
    }

    appendText(text: string, editorView: EditorView) {
        const transaction: Transaction = editorView.state.tr;
        transaction.insertText(text);
        transaction.scrollIntoView();
        editorView.dispatch(transaction);
    }

    replaceText(text: string, editorView: EditorView) {
        const transaction: Transaction = editorView.state.tr;
        transaction.deleteSelection();
        transaction.insertText(text);
        transaction.scrollIntoView();
        editorView.dispatch(transaction);
    }

    // prima chiamato con il click sull selettore suggerimenti e con Fn.. | no, ora mai chiamata sembrerebbe
    replaceCurrentParagraph(text: string, cursorNextParagraph: boolean, editorView: EditorView) {
        const transaction = editorView.state.tr;
        const selection = editorView.state.selection;
        let marks = null;

        if (selection.$from.nodeBefore) {
            marks = selection.$from.nodeBefore.marks;
        } else if (selection.$from.nodeAfter) {
            marks = selection.$from.nodeAfter.marks;
        }
        transaction.delete(selection.$from.start(), selection.$from.end());
        if (marks) {
            transaction.ensureMarks(marks);
        }
        transaction.insertText(text);

        if (cursorNextParagraph) {

            marks = editorView.state.storedMarks || (editorView.state.selection.$to.parentOffset && editorView.state.selection.$from.marks());
            transaction.split(transaction.mapping.map(selection.$to.pos));
        }

        if (marks) {
            transaction.ensureMarks(marks);
        }
        transaction.scrollIntoView();
        editorView.dispatch(transaction);
    }

    // chiamato con autocompletamento e con Fn..
    insertWithinCurrentParagraph(filter: string, text: any, cursorNextParagraph: boolean, editorView: EditorView) {
        const state = editorView.state;
        const transaction = state.tr;
        const selection = state.selection;
        const from = (selection.from - filter.length);
        const to = selection.to;

        let marks = null;

        // cursorNextParagraph = false;

        if (!wrapInList(state.schema.nodes.bullet_list)(state)) {
            cursorNextParagraph = false;
        }

        const domNode = document.createElement('div');
        domNode.innerHTML = text;
        const newDoc = DOMParser.fromSchema(state.schema).parse(domNode, { preserveWhitespace: false });
        transaction.replaceRangeWith(from, to, newDoc);


        const parentSize = state.doc.resolve(selection.head).parent.content.size
        if (selection.empty) {
            if (parentSize == 0 || parentSize == filter.length) {
                transaction.deleteRange(from - 1, from);
            } else if (filter) {
                transaction.deleteRange(from, from);
            }
        }

        if (cursorNextParagraph) {
            marks = state.storedMarks || (selection.$to.parentOffset && selection.$from.marks());

            const emptyNode = state.schema.nodes.paragraph.createAndFill();
            if (emptyNode) {
                transaction.insert(transaction.doc.content.size - 1, emptyNode);
            }
        }

        if (marks) {
            transaction.ensureMarks(marks);
        }

        // transaction.setSelection(new TextSelection(transaction.doc.resolve(transaction.doc.content.size - 2)));// OLD

        let cursorPosition = newDoc.nodeSize + from - 2;
        if (transaction.doc.content.size === newDoc.nodeSize) {
            cursorPosition -= 2;
        }
        if (parentSize === 0) {
            cursorPosition -= 2;
        }
        if (cursorPosition >= transaction.doc.content.size) {
            cursorPosition = transaction.doc.content.size - 2;
        }
        // cursorPosition+=2;

        // console.log('contentSize: ', transaction.doc.content.size, ' nodeSize: ', newDoc.nodeSize);
        // console.log('from: ', from, ' to: ', to);
        // console.log('parentSize: ', parentSize);
        // console.log('cursorPosition: ', cursorPosition);
        // console.log('cursorNextParagraph: ', cursorNextParagraph);

        transaction.setSelection(new TextSelection(transaction.doc.resolve(cursorPosition)));

        transaction.scrollIntoView();
        editorView.dispatch(transaction);
        editorView.focus();
    }


    replaceContent(newContent: string, editorView: EditorView, includeInHistory: boolean): void {
        if (newContent == null) {
            newContent = '';
        }
        const transaction = editorView.state.tr;
        const domNode = document.createElement('div');
        domNode.innerHTML = newContent;
        const newDoc = DOMParser.fromSchema(editorView.state.schema).parse(domNode);
        transaction.replaceRangeWith(0, transaction.doc.content.size, newDoc);
        transaction.setMeta('addToHistory', includeInHistory);
        editorView.dispatch(transaction);
    }

    clear(contentConfiguration: EditorContentConfiguration, editorView: EditorView): void {
        const transaction = editorView.state.tr;
        transaction.replaceRangeWith(0, transaction.doc.content.size, this.contentBuilder.createParagraph(contentConfiguration));
        transaction.setMeta('addToHistory', 0);
        transaction.scrollIntoView();
        editorView.dispatch(transaction);
    }



    createStartDoc(contentConfiguration: EditorContentConfiguration): Node {
        return this.contentBuilder.createStartDoc(contentConfiguration);
    }

    addMark(editorView: EditorView, style: string) {
        const transaction = editorView.state.tr;
        const selection = editorView.state.selection;
        const mark = editorView.state.schema.mark(style);
        if (mark) {
            if (selection.from === selection.to) {
                transaction.addStoredMark(mark);
            } else {
                transaction.addMark(selection.from, selection.to, mark);
            }
            editorView.dispatch(transaction);
        }
    }

    removeMark(editorView: EditorView, style: string) {
        const transaction = editorView.state.tr;
        const selection = editorView.state.selection;
        const mark = editorView.state.schema.mark(style);
        if (mark) {
            if (selection.from === selection.to) {
                transaction.removeStoredMark(mark);
            } else {
                transaction.removeMark(selection.from, selection.to, mark);
            }
            editorView.dispatch(transaction);
        }
    }

    splitCurrentParagraph(editorView: EditorView) {
        const transaction = editorView.state.tr;
        const state = editorView.state;
        const selection = state.selection;
        const marks = state.storedMarks || (state.selection.$to.parentOffset && state.selection.$from.marks());
        // const storedMark = state.selection.$from.nodeBefore.marks; // you may need to filter some marks like link, etc
        const attrs = state.selection.$from.parent.attrs;

        if (wrapInList(state.schema.nodes.bullet_list)(state)) {
            // non siamo in elenco puntato, spezzo il paragrafo
            transaction.split(selection.$from.pos).scrollIntoView();
            if (marks) {
                transaction.ensureMarks(marks);
            }
            editorView.dispatch(transaction);
        } else {
            if (selection.$to.nodeBefore) { // se prima ho un elenco puntato
                splitListItem(state.schema.nodes.list_item)(state, editorView.dispatch); // perde il font-family
                const fontFamily = { name: 'fontFamily', value: attrs.fontFamily };
                this.updateDocumentAttribute(editorView, fontFamily);
                const fontSize = { name: 'fontSize', value: attrs.fontSize };
                this.updateDocumentAttribute(editorView, fontSize);
                if (marks.length) {
                    marks.forEach(m => {
                        this.addMark(editorView, m.type.name);
                    });
                }
            } else { // se non ho un elenco puntato (ho fatto invio due volte)
                liftListItem(state.schema.nodes.list_item)(state, editorView.dispatch);
            }
        }
    }
}
